import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress
} from "@mui/material";
import {useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CheckIcon from "@mui/icons-material/Check";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {isAfter, isBefore} from "date-fns";
import TextField from "@mui/material/TextField";
import plusSign from "../../assets/plus-sign.svg"
import $ from "jquery";
import apiURL from "../../config/environment";
import {format} from "date-fns";
import {ClearScheduleDates} from "../icons/ClearScheduleDates";

export const AddScheduleDialog = ({
    open,
    handleClose,
    existingRows,
    setExistingRows
}) => {
    const [name, setName] = useState("");
    const [schedules, setSchedules] = useState([
        {id: 0, startDate: null, endDate: null},
        {id: 1, startDate: null, endDate: null},
        {id: 2, startDate: null, endDate: null},
    ])

    const [saveError, setSaveError] = useState(false);
    const [nameError, setNameError] = useState("");
    const [saving, setSaving] = useState(false);
    const [dateError, setDateError] = useState(new Map());

    const handleDialogClose = () => {
        setName("");
        setSchedules([
            {id: 0, startDate: null, endDate: null},
            {id: 1, startDate: null, endDate: null},
            {id: 2, startDate: null, endDate: null},
        ])
        setSaveError(false);
        setNameError("")
        setDateError(new Map());
        handleClose();
    }

    const startDateOnChange = (schedule, nv) => {

        setSchedules((prev) => {
            return prev.map(s => s.id === schedule.id ? {...s, startDate: nv} : s);
        });
        if (nv && schedule.endDate && isAfter(nv, schedule.endDate)) {
            setDateError((prev) => {
                const newError = new Map(prev);
                newError.set(schedule.id + "_start", "Start Date cannot be after End Date");
                return newError;
            });
            return;
        }
        setDateError((prev) => {
            if (prev.has(schedule.id + "_start")) {
                const newError = new Map(prev);
                newError.delete(schedule.id + "_start");
                return newError;
            }
            return prev;
        });
    }

    const handleEndDateOnChange = (schedule, nv) => {
        setSchedules((prev) => {
            return prev.map(s => s.id === schedule.id ? {...s, endDate: nv} : s);
        });
        if (nv && !schedule.startDate) {
            setDateError((prev) => {
                const newError = new Map(prev);
                newError.set(schedule.id + "_start", "Start Date is required");
                return newError;
            });
            return;
        }
        if (nv && schedule.startDate && isBefore(nv, schedule.startDate)) {
            setDateError((prev) => {
                const newError = new Map(prev);
                newError.set(schedule.id + "_end", "End Date cannot be before Start Date");
                return newError;
            });
            return;
        }
        setDateError((prev) => {
            if (prev.has(schedule.id + "_end")) {
                const newError = new Map(prev);
                newError.delete(schedule.id + "_end");
                return newError;
            }
            return prev;
        });
    }

    const handleAddRow = () => {
        setSchedules((prev) => {
            return [...prev, {id: prev.length, startDate: null, endDate: null}];
        })
    }

    const handleSave = () => {
        const validSchedules = schedules.filter((s) => s.startDate !== null);
        if (validSchedules.length === 0 || name === "" || nameError) {
            return;
        }

        if (existingRows.some(row => row.name.toLowerCase() === name.toLowerCase())) {
            setNameError("Name already exists");
            return;
        }

        setSaving(true);
        const token = JSON.parse(localStorage.getItem("userInfo"))?.token;
        $.ajax({
            method: "POST",
            url: apiURL + "admin/schedule",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            contentType: "application/json",
            data: JSON.stringify({
                ranges: validSchedules.map((s) => {
                    return {
                        start: format(s.startDate, "yyyy-MM-dd"),
                        end: s.endDate ? format(s.endDate, "yyyy-MM-dd") : null
                    }
                }),
                name
            }),
        })
            .done((data) => {
                setSaving(false);
                setExistingRows([...existingRows, data]);
                handleDialogClose();
            })
            .fail((jqXHR, textStatus, errorThrows) => {
                setSaving(false);
                console.error(
                    "Error adding bike counter",
                    jqXHR,
                    textStatus,
                    errorThrows
                );
                setSaveError(true);
            });
    }

    return (
        <Dialog
            open={open}
            onClose={() => {
                handleDialogClose();
            }}
        >
            <Box sx={{ width: "444px", padding: "16px 24px 24px 24px" }}>
                <DialogTitle sx={{ padding: 0 }} id="form-dialog-title">
                    New Schedule
                </DialogTitle>


                <DialogContent sx={{ padding: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                            marginTop: "16px",
                        }}
                    >
                        {saveError && (
                            <Alert severity={"error"}>
                                Unable to save. Please try again later.
                            </Alert>
                        )}

                        <TextField
                            label={"Name"}
                            error={nameError}
                            helperText={nameError}
                            onChange={(e) => {
                                const lowerCasedNewVal = e.target.value.trim().toLowerCase();
                                setName(e.target.value);
                                if (existingRows.some(row => row.name.toLowerCase() === e.target.value.toLowerCase())) {
                                    setNameError("Name already exists");
                                } else if (lowerCasedNewVal === "always" || lowerCasedNewVal === "never") {
                                    setNameError("Name cannot be 'Always' or 'Never'");
                                } else if (name.length > 100) {
                                    setNameError("Name cannot be longer than 100 characters");
                                } else {
                                    setNameError(false);
                                }
                            }}
                            autoFocus
                            value={name}
                            />

                        {schedules.map((schedule) => {
                            return (
                                <Box key={schedule.id} sx={{ display: "flex", gap: "16px" }}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker label="Start Date"
                                                    value={schedule.startDate}
                                                    onChange={(nv) => {
                                                        startDateOnChange(schedule, nv)
                                                    }}
                                                    maxDate={schedule.endDate || null}
                                                    slotProps={{
                                                        textField: {
                                                            helperText: dateError.get(schedule.id + "_start") || "",
                                                        }
                                                    }}
                                                    format={"yyyy-MM-dd"}
                                        />
                                    </LocalizationProvider>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker label="End Date"
                                                    value={schedule.endDate}
                                                    onChange={(nv) => {
                                                        handleEndDateOnChange(schedule, nv)
                                                    }}
                                                    minDate={schedule.startDate || null}
                                                    slotProps={{
                                                        textField: {
                                                            helperText: dateError.get(schedule.id + "_end") || "",
                                                        }
                                                    }}
                                                    format={"yyyy-MM-dd"}
                                        />
                                    </LocalizationProvider>
                                    <Box sx={{display: "flex", alignItems: "center"}}>
                                    <Button
                                        sx={{display: "flex", alignItems: "center", width: "40px", height: "40px"}}
                                        disabled={!schedule.startDate && !schedule.endDate}
                                        onClick={() => {
                                            setSchedules((prev) => {
                                                return prev.map(s => s.id === schedule.id ? {...s, startDate: null, endDate: null} : s);
                                            });
                                            setDateError((prev) => {
                                                const newError = new Map(prev);
                                                newError.delete(schedule.id + "_start");
                                                newError.delete(schedule.id + "_end");
                                                return newError;
                                            });
                                        }}
                                    >
                                        <ClearScheduleDates active={schedule.startDate || schedule.endDate} />
                                    </Button>
                                    </Box>
                                </Box>
                            )
                        })}

                        {schedules.filter((s) => s.startDate !== null).length >= 3 ? (
                        <Box>
                        <Button onClick={() => handleAddRow()}><img src={plusSign} alt={"Add Row"} />ADD ROW</Button>
                        </Box>) : null}

                    </Box>
                </DialogContent>


                <DialogActions sx={{ padding: 0, marginTop: "16px" }}>
                    <Button
                        disabled={saving}
                        onClick={() => {
                            handleDialogClose();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={
                            saving || name === "" || nameError || schedules.filter((s) => s.startDate !== null).length === 0 || dateError.size > 0
                        }
                        variant={"contained"}
                        sx={{
                            backgroundColor: "rgba(19, 151, 203, 1)",
                            gap: "8px",
                            width: "91.75px",
                            height: "36.5px",
                        }}
                    >
                        {saving ? (<CircularProgress size={"24.5px"} />) : (
                            <>
                                <CheckIcon sx={{ width: "16px", height: "24px" }} />
                                Save
                            </>
                        )}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    )
}
